import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../../components/product/ProductCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import productService from '../../services/product.service';
import { FiSearch, FiFilter } from 'react-icons/fi';
import { useLanguage } from '../../context/LanguageContext';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLanguage();

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: searchParams.get('sort') || '-createdAt',
    page: parseInt(searchParams.get('page')) || 1,
  });

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = { limit: 12 };
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params[key] = value;
        });
        const { data } = await productService.getAll(params);
        setProducts(data.data);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters, lang]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
    window.scrollTo(0, 0);
  };

  return (
    <div className="section-padding">
      <div className="container">
        <h2 className="mb-4">{t('allProductsTitle')}</h2>

        {/* Search & Filters */}
        <div className="row g-3 mb-4">
          <div className="col-md-5">
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('searchProducts')}
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
                <button className="btn btn-primary" type="submit">{t('search')}</button>
              </div>
            </form>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={filters.sort}
              onChange={(e) => setFilters({ ...filters, sort: e.target.value, page: 1 })}
            >
              <option value="-createdAt">{t('newestFirst')}</option>
              <option value="price">{t('priceLowHigh')}</option>
              <option value="-price">{t('priceHighLow')}</option>
              <option value="-ratings.average">{t('topRated')}</option>
            </select>
          </div>
          <div className="col-md-2">
            <input
              type="number"
              className="form-control"
              placeholder={t('minPrice')}
              value={filters.minPrice}
              onChange={(e) => setFilters({ ...filters, minPrice: e.target.value, page: 1 })}
            />
          </div>
          <div className="col-md-2">
            <input
              type="number"
              className="form-control"
              placeholder={t('maxPrice')}
              value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value, page: 1 })}
            />
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="row g-4">
              {products.map((product) => (
                <div key={product._id} className="col-sm-6 col-md-4 col-lg-3">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>

            {products.length === 0 && (
              <div className="text-center py-5">
                <FiFilter size={48} className="text-muted mb-3" />
                <h5>{t('noProductsFound')}</h5>
                <p className="text-muted">Try adjusting your search or filters.</p>
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <nav className="mt-5 d-flex justify-content-center">
                <ul className="pagination">
                  <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(pagination.page - 1)}>
                      Previous
                    </button>
                  </li>
                  {Array.from({ length: pagination.pages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${pagination.page >= pagination.pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(pagination.page + 1)}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
